/**
 * MobileAceStep v3 — AceStep music generation for Telegram Mini App.
 *
 * Communicates exclusively with the FastAPI proxy at /v1/acestep/* which calls
 * a Dockerised Gradio AceStep container via gradio_client (Python side).
 *
 * Tabs: Generate | Library | Status
 */

import React, { useState, useEffect, useRef, useCallback } from 'react';

// ─── Types ──────────────────────────────────────────────────────────────────

interface MobileAceStepProps {
  triggerHaptic: (style: 'light' | 'medium' | 'heavy') => void;
}

type TaskType   = 'text2music' | 'sound_effect' | 'cover' | 'repaint' | 'speech';
type AudioFormat = 'mp3' | 'wav' | 'flac';
type Tab        = 'generate' | 'library' | 'status';

interface GeneratedTrack {
  id:        string;
  audioUrl:  string;
  taskType:  TaskType;
  prompt:    string;
  createdAt: number;
  format:    AudioFormat;
}

// ─── Parametric style helpers ────────────────────────────────────────────────

function tabStyle(active: boolean): React.CSSProperties {
  return {
    flex: 1,
    padding: '7px 4px',
    borderRadius: 9,
    border: 'none',
    background: active ? 'linear-gradient(135deg, #7c3aed, #2563eb)' : 'transparent',
    color: active ? '#fff' : 'rgba(255,255,255,0.5)',
    fontSize: 12,
    fontWeight: active ? 600 : 400,
    cursor: 'pointer',
    transition: 'all 0.2s ease',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    gap: 4,
  };
}

function generateBtnStyle(disabled: boolean): React.CSSProperties {
  return {
    width: '100%',
    padding: '14px',
    borderRadius: 12,
    border: 'none',
    background: disabled
      ? 'rgba(124,58,237,0.3)'
      : 'linear-gradient(135deg, #7c3aed 0%, #2563eb 100%)',
    color: disabled ? 'rgba(255,255,255,0.4)' : '#fff',
    fontSize: 15,
    fontWeight: 700,
    cursor: disabled ? 'not-allowed' : 'pointer',
    transition: 'all 0.2s ease',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    gap: 8,
    marginTop: 4,
    boxShadow: disabled ? 'none' : '0 4px 20px rgba(124,58,237,0.4)',
  };
}

function progressBarStyle(pct: number): React.CSSProperties {
  return {
    height: '100%',
    width: `${pct}%`,
    background: 'linear-gradient(90deg, #7c3aed, #2563eb)',
    transition: 'width 0.3s ease',
    borderRadius: 10,
  };
}

function statusDotStyle(online: boolean): React.CSSProperties {
  return {
    width: 8,
    height: 8,
    borderRadius: '50%',
    background: online ? '#4ade80' : '#f87171',
    flexShrink: 0,
    boxShadow: online ? '0 0 8px #4ade80' : 'none',
  };
}

// ─── Static styles ───────────────────────────────────────────────────────────

const S: Record<string, React.CSSProperties> = {
  root: {
    display: 'flex',
    flexDirection: 'column',
    height: '100%',
    background: 'linear-gradient(160deg, #0d0d1a 0%, #1a0d2e 50%, #0d1a2e 100%)',
    color: '#e8e8f8',
    fontFamily: "'Inter', -apple-system, sans-serif",
    overflow: 'hidden',
  },
  header: {
    padding: '16px 20px 12px',
    borderBottom: '1px solid rgba(255,255,255,0.07)',
    background: 'rgba(13,13,26,0.9)',
    backdropFilter: 'blur(16px)',
    flexShrink: 0,
  },
  headerRow: {
    display: 'flex',
    alignItems: 'center',
    gap: 10,
    marginBottom: 12,
  },
  logo: {
    width: 32,
    height: 32,
    borderRadius: 8,
    background: 'linear-gradient(135deg, #7c3aed, #2563eb)',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    fontSize: 16,
    flexShrink: 0,
  },
  headerTitle: {
    fontSize: 18,
    fontWeight: 700,
    background: 'linear-gradient(90deg, #a78bfa, #60a5fa)',
    WebkitBackgroundClip: 'text',
    WebkitTextFillColor: 'transparent',
  },
  tabBar: {
    display: 'flex',
    gap: 4,
    background: 'rgba(255,255,255,0.05)',
    borderRadius: 12,
    padding: '3px',
  },
  body: {
    flex: 1,
    overflowY: 'auto',
    padding: '16px',
  },
  label: {
    fontSize: 12,
    fontWeight: 600,
    color: 'rgba(255,255,255,0.5)',
    letterSpacing: '0.05em',
    textTransform: 'uppercase',
    marginBottom: 6,
    display: 'block',
  },
  field: {
    marginBottom: 14,
  },
  textarea: {
    width: '100%',
    background: 'rgba(255,255,255,0.06)',
    border: '1px solid rgba(255,255,255,0.12)',
    borderRadius: 10,
    padding: '10px 12px',
    color: '#e8e8f8',
    fontSize: 14,
    resize: 'none',
    outline: 'none',
    lineHeight: 1.5,
    boxSizing: 'border-box',
    fontFamily: 'inherit',
  },
  select: {
    width: '100%',
    background: 'rgba(255,255,255,0.06)',
    border: '1px solid rgba(255,255,255,0.12)',
    borderRadius: 10,
    padding: '10px 12px',
    color: '#e8e8f8',
    fontSize: 14,
    outline: 'none',
    boxSizing: 'border-box',
  },
  row: {
    display: 'grid',
    gridTemplateColumns: '1fr 1fr',
    gap: 10,
  },
  slider: {
    flex: 1,
    accentColor: '#7c3aed',
    cursor: 'pointer',
    width: '100%',
  },
  progressWrap: {
    background: 'rgba(255,255,255,0.06)',
    borderRadius: 10,
    overflow: 'hidden',
    height: 6,
    marginTop: 8,
  },
  trackCard: {
    background: 'rgba(255,255,255,0.05)',
    border: '1px solid rgba(255,255,255,0.1)',
    borderRadius: 14,
    padding: 14,
    marginBottom: 12,
  },
  trackMeta: {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'space-between',
    marginBottom: 8,
  },
  trackLabel: {
    fontSize: 12,
    color: 'rgba(255,255,255,0.5)',
  },
  trackPrompt: {
    fontSize: 13,
    fontWeight: 600,
    color: '#e8e8f8',
    marginBottom: 10,
    overflow: 'hidden',
    textOverflow: 'ellipsis',
    whiteSpace: 'nowrap',
  },
  statusCard: {
    background: 'rgba(255,255,255,0.05)',
    border: '1px solid rgba(255,255,255,0.1)',
    borderRadius: 14,
    padding: 16,
    marginBottom: 12,
  },
  badge: {
    padding: '3px 8px',
    borderRadius: 6,
    fontSize: 11,
    fontWeight: 700,
    background: 'rgba(124,58,237,0.3)',
    color: '#a78bfa',
    border: '1px solid rgba(124,58,237,0.4)',
  },
  emptyState: {
    textAlign: 'center',
    color: 'rgba(255,255,255,0.3)',
    padding: '40px 20px',
    fontSize: 14,
  },
  errorBox: {
    background: 'rgba(248,113,113,0.1)',
    border: '1px solid rgba(248,113,113,0.3)',
    borderRadius: 10,
    padding: '10px 14px',
    fontSize: 13,
    color: '#fca5a5',
    marginBottom: 12,
  },
};

// ─── Constants ───────────────────────────────────────────────────────────────

const TASK_MODES: { id: TaskType; icon: string; label: string }[] = [
  { id: 'text2music',   icon: '🎵', label: 'Music' },
  { id: 'sound_effect', icon: '🔊', label: 'SFX'   },
  { id: 'cover',        icon: '🎤', label: 'Cover' },
  { id: 'repaint',      icon: '🎨', label: 'Edit'  },
];

const TRACK_PARTS = [
  'vocals',
  'backing_vocals',
  'drums',
  'bass',
  'guitar',
  'keyboard',
  'synth',
  'strings',
  'percussion',
  'woodwinds',
  'brass',
  'fx',
];

function toPlayableUrl(url: string): string {
  try {
    const parsed = new URL(url, window.location.origin);
    if (
      ['localhost', '127.0.0.1', '0.0.0.0'].includes(parsed.hostname) &&
      parsed.pathname.startsWith('/v1/acestep/')
    ) {
      return `${window.location.origin}${parsed.pathname}${parsed.search}`;
    }
    return parsed.toString();
  } catch {
    return url;
  }
}

function extensionFromType(contentType: string | null, fallback: AudioFormat): string {
  if (contentType?.includes('wav')) return 'wav';
  if (contentType?.includes('flac')) return 'flac';
  if (contentType?.includes('mpeg') || contentType?.includes('mp3')) return 'mp3';
  return fallback;
}

// ─── Toggle component ────────────────────────────────────────────────────────

function Toggle({ on, onChange }: { on: boolean; onChange: () => void }) {
  return (
    <div
      onClick={onChange}
      style={{
        width: 40,
        height: 22,
        borderRadius: 11,
        background: on ? '#7c3aed' : 'rgba(255,255,255,0.15)',
        cursor: 'pointer',
        position: 'relative',
        transition: 'background 0.2s',
        flexShrink: 0,
      }}
    >
      <div style={{
        position: 'absolute',
        top: 2,
        left: on ? 20 : 2,
        width: 18,
        height: 18,
        borderRadius: '50%',
        background: '#fff',
        transition: 'left 0.2s',
      }} />
    </div>
  );
}

// ─── Main Component ──────────────────────────────────────────────────────────

export default function MobileAceStep({ triggerHaptic }: MobileAceStepProps) {
  const [tab, setTab]               = useState<Tab>('generate');

  // Generate form
  const [taskType, setTaskType]     = useState<TaskType>('text2music');
  const [prompt, setPrompt]         = useState('');
  const [lyrics, setLyrics]         = useState('');
  const [instrumental, setInstr]    = useState(false);
  const [duration, setDuration]     = useState(30);
  const [durationAuto, setDurationAuto] = useState(false);
  const [steps, setSteps]           = useState(8);
  const [guidance, setGuidance]     = useState(7.0);
  const [format, setFormat]         = useState<AudioFormat>('mp3');
  const [thinking, setThinking]     = useState(true);
  const [enhanceCaption, setEnhanceCaption] = useState(false);
  const [enhanceLyrics, setEnhanceLyrics] = useState(false);
  const [vocLang, setVocLang]       = useState('en');
  
  // New AceStep 1.5 fields
  const [bpm, setBpm]               = useState('auto');
  const [keyScale, setKeyScale]     = useState('auto');
  const [timeSignature, setTimeSig] = useState('4/4');
  const [trackName, setTrackName]   = useState('vocals');
  const [referenceAudio, setReferenceAudio] = useState<string | null>(null);

  const [generating, setGenerating] = useState(false);
  const [progress, setProgress]     = useState(0);
  const [genError, setGenError]     = useState<string | null>(null);
  const timerRef                    = useRef<ReturnType<typeof setInterval> | null>(null);

  // Library
  const [library, setLibrary]       = useState<GeneratedTrack[]>([]);
  const [playingId, setPlayingId]   = useState<string | null>(null);
  const [libraryError, setLibraryError] = useState<string | null>(null);
  const audioRef                    = useRef<HTMLAudioElement | null>(null);

  // Status
  const [online, setOnline]         = useState<boolean | null>(null);
  const [checkingStatus, setChecking] = useState(false);

  // ── Status check ──
  const checkStatus = useCallback(async () => {
    setChecking(true);
    try {
      const r = await fetch('/v1/acestep/status');
      const d = await r.json();
      setOnline(d.api_running === true);
    } catch {
      setOnline(false);
    } finally {
      setChecking(false);
    }
  }, []);

  useEffect(() => { checkStatus(); }, [checkStatus]);

  // ── Progress animation ──
  const startProgress = () => {
    setProgress(0);
    let pct = 0;
    timerRef.current = setInterval(() => {
      pct = pct + (95 - pct) * 0.04;
      setProgress(Math.min(pct, 94));
    }, 800);
  };
  const stopProgress = (success: boolean) => {
    if (timerRef.current) clearInterval(timerRef.current);
    setProgress(success ? 100 : 0);
    if (success) setTimeout(() => setProgress(0), 1500);
  };

  // ── File upload ──
  const handleFileChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (file) {
      const reader = new FileReader();
      reader.onload = () => setReferenceAudio(reader.result as string);
      reader.readAsDataURL(file);
    } else {
      setReferenceAudio(null);
    }
  };

  // ── Generate ──
  const handleGenerate = async () => {
    if (!prompt.trim() && taskType !== 'cover' && taskType !== 'repaint') { 
      setGenError('Please enter a description.'); 
      return; 
    }
    if ((taskType === 'cover' || taskType === 'repaint') && !referenceAudio) {
      setGenError('Please upload an audio file for this mode.');
      return;
    }
    if (generating) return;

    triggerHaptic('medium');
    setGenerating(true);
    setGenError(null);
    startProgress();

    try {
      const baseBody = {
        prompt:          prompt.trim(),
        lyrics:          instrumental ? '[instrumental]' : lyrics.trim(),
        task_type:       taskType,
        audio_duration:  durationAuto ? -1 : duration,
        inference_steps: steps,
        guidance_scale:  guidance,
        seed:            -1,
        batch_size:      1,
        audio_format:    format,
        thinking,
        use_cot_caption: enhanceCaption,
        enhance_lyrics:  enhanceLyrics,
        vocal_language:  vocLang,
        instrumental,
        bpm,
        key_scale:       keyScale,
        time_signature:  timeSignature,
        track_name:      trackName
      };

      const body: any = { ...baseBody };
      if (taskType === 'cover') {
        body.reference_audio_base64 = referenceAudio;
      }
      if (taskType === 'text2music' && referenceAudio) {
        body.reference_audio_base64 = referenceAudio;
      }
      if (taskType === 'repaint') {
        body.src_audio_base64 = referenceAudio;
      }

      const r = await fetch('/v1/acestep/generate', {
        method:  'POST',
        headers: { 'Content-Type': 'application/json' },
        body:    JSON.stringify(body),
      });

      if (!r.ok) {
        const err = await r.json().catch(() => ({ detail: r.statusText }));
        throw new Error(err.detail || `HTTP ${r.status}`);
      }

      const data = await r.json();
      stopProgress(true);
      triggerHaptic('heavy');

      if (data.audio_url) {
        const track: GeneratedTrack = {
          id:        Date.now().toString(),
          audioUrl:  data.audio_url,
          taskType,
          prompt:    prompt.trim(),
          createdAt: Date.now(),
          format,
        };
        setLibrary(prev => [track, ...prev]);
        setTab('library');
      } else {
        setGenError('Generation complete but no audio URL returned.');
      }
    } catch (e: unknown) {
      stopProgress(false);
      triggerHaptic('light');
      const msg = e instanceof Error ? e.message : 'Unknown error';
      setGenError(msg);
    } finally {
      setGenerating(false);
    }
  };

  // ── Playback ──
  const handlePlay = async (track: GeneratedTrack) => {
    setLibraryError(null);
    // Stop any currently playing audio
    if (playingId === track.id) {
      audioRef.current?.pause();
      setPlayingId(null);
      return;
    }
    audioRef.current?.pause();

    try {
      // Fetch the audio as a blob to avoid cross-origin streaming issues in the MiniApp.
      const response = await fetch(toPlayableUrl(track.audioUrl), { cache: 'no-store' });
      if (!response.ok) throw new Error('Failed to fetch audio');
      const blob = await response.blob();
      const objectUrl = URL.createObjectURL(blob);
      const audio = new Audio(objectUrl);
      audioRef.current = audio;
      audio.play().catch(() => {});
      setPlayingId(track.id);
      audio.onended = () => {
        setPlayingId(null);
        URL.revokeObjectURL(objectUrl);
      };
      triggerHaptic('light');
    } catch (e) {
      // Fallback: try direct URL if blob fetch fails
      const audio = new Audio(toPlayableUrl(track.audioUrl));
      audioRef.current = audio;
      audio.play().catch(() => {});
      setPlayingId(track.id);
      audio.onended = () => setPlayingId(null);
    }
  };

  const handleDownload = async (track: GeneratedTrack) => {
    triggerHaptic('light');
    setLibraryError(null);
    const webApp = (window as any).Telegram?.WebApp;
    const directUrl = toPlayableUrl(track.audioUrl);
    const fileName = `ace-step-${track.createdAt}.${track.format}`;

    // ── Strategy 1: Telegram native download (best UX in WebView) ──
    // Uses the real HTTP URL — Telegram client handles the download natively.
    // This is the ONLY reliable method inside Telegram WebView because:
    //   - fetch() to cross-origin URLs is blocked by CORS
    //   - document.createElement('a').click() does not work in WebView
    if (typeof webApp?.downloadFile === 'function') {
      try {
        webApp.downloadFile(
          { url: directUrl, file_name: fileName },
          (accepted: boolean) => {
            if (!accepted) {
              setLibraryError('Telegram did not accept the download request.');
            }
          },
        );
        return; // Telegram handles everything from here
      } catch {
        // Fall through to fetch+blob fallback
      }
    }

    // ── Strategy 2: fetch + blob + manual download (outside Telegram) ──
    try {
      const response = await fetch(directUrl, { cache: 'no-store' });
      if (!response.ok) throw new Error(`Server returned ${response.status}`);

      const blob = await response.blob();

      // Verify we got actual audio data, not an error page
      if (blob.size < 1024) {
        throw new Error(`Downloaded file is too small (${blob.size} bytes). The server may have returned an error page.`);
      }

      const ext = extensionFromType(response.headers.get('content-type'), track.format);
      const objectUrl = URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = objectUrl;
      link.download = `ace-step-${track.createdAt}.${ext}`;
      link.rel = 'noopener';
      document.body.appendChild(link);
      link.click();
      link.remove();
      setTimeout(() => URL.revokeObjectURL(objectUrl), 1000);
    } catch (e: unknown) {
      const msg = e instanceof Error ? e.message : 'Unknown error';
      const message = `Download failed: ${msg}`;
      setLibraryError(message);
      if (webApp?.showAlert) webApp.showAlert(message);
    }
  };

  const deleteTrack = (id: string) => {
    if (playingId === id) { audioRef.current?.pause(); setPlayingId(null); }
    setLibrary(prev => prev.filter(t => t.id !== id));
    triggerHaptic('light');
  };

  const fmtTime = (ms: number) =>
    new Date(ms).toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' });

  // ────────────────────────────────────────────────────────────────────────────
  return (
    <div style={S.root}>

      {/* ── Header ── */}
      <div style={S.header}>
        <div style={S.headerRow}>
          <div style={S.logo}>🎼</div>
          <span style={S.headerTitle}>AceStep</span>
          {online !== null && (
            <div style={{ marginLeft: 'auto', display: 'flex', alignItems: 'center', gap: 6 }}>
              <div style={statusDotStyle(online)} />
              <span style={{ fontSize: 11, color: 'rgba(255,255,255,0.4)' }}>
                {online ? 'ready' : 'offline'}
              </span>
            </div>
          )}
        </div>

        <div style={S.tabBar}>
          {([
            { id: 'generate' as Tab, icon: '✨', label: 'Generate'                                },
            { id: 'library'  as Tab, icon: '🎧', label: library.length ? `Library (${library.length})` : 'Library' },
            { id: 'status'   as Tab, icon: '⚡', label: 'Status'                                  },
          ]).map(t => (
            <button key={t.id} style={tabStyle(tab === t.id)}
              onClick={() => { setTab(t.id); triggerHaptic('light'); }}>
              {t.icon} {t.label}
            </button>
          ))}
        </div>
      </div>

      {/* ── Body ── */}
      <div style={S.body}>

        {/* ════════ GENERATE ════════ */}
        {tab === 'generate' && (
          <div>
            {/* Task type pills */}
            <div style={{ ...S.field, display: 'flex', gap: 8 }}>
              {TASK_MODES.map(m => (
                <button key={m.id} onClick={() => { setTaskType(m.id); triggerHaptic('light'); }}
                  style={{
                    padding: '7px 14px',
                    borderRadius: 20,
                    border: '1px solid',
                    borderColor: taskType === m.id ? '#7c3aed' : 'rgba(255,255,255,0.12)',
                    background: taskType === m.id ? 'rgba(124,58,237,0.25)' : 'rgba(255,255,255,0.04)',
                    color: taskType === m.id ? '#a78bfa' : 'rgba(255,255,255,0.5)',
                    fontSize: 13, fontWeight: 600, cursor: 'pointer', transition: 'all 0.2s',
                  }}>
                  {m.icon} {m.label}
                </button>
              ))}
            </div>

            {/* Prompt */}
            <div style={S.field}>
              <label style={S.label}>Description / Prompt *</label>
              <textarea
                style={{ ...S.textarea, height: 72 }}
                placeholder={taskType === 'sound_effect'
                  ? 'e.g. rain falling on a tin roof, distant thunder…'
                  : 'e.g. upbeat lo-fi hip hop with jazzy chords, calm mood…'}
                value={prompt}
                onChange={e => setPrompt(e.target.value)}
              />
            </div>

            {/* Lyrics (text2music only) */}
            {taskType === 'text2music' && (
              <>
                <div style={{ ...S.field, display: 'flex', alignItems: 'center', gap: 10, marginBottom: 8 }}>
                  <label style={{ ...S.label, margin: 0 }}>Instrumental</label>
                  <Toggle on={instrumental} onChange={() => { setInstr(p => !p); triggerHaptic('light'); }} />
                </div>
                {!instrumental && (
                  <div style={S.field}>
                    <label style={S.label}>Lyrics</label>
                    <textarea style={{ ...S.textarea, height: 80 }}
                      placeholder={'[verse]\nYour lyrics here…'}
                      value={lyrics}
                      onChange={e => setLyrics(e.target.value)}
                    />
                  </div>
                )}
              </>
            )}

            {/* Audio Upload */}
            {(taskType === 'text2music' || taskType === 'cover' || taskType === 'repaint') && (
              <div style={S.field}>
                <label style={S.label}>
                  Reference Audio File {taskType === 'text2music' ? '(optional)' : '*'}
                </label>
                <input 
                  type="file" 
                  accept="audio/*" 
                  onChange={handleFileChange}
                  style={{
                    display: 'block', width: '100%', padding: '10px',
                    background: 'rgba(255,255,255,0.05)',
                    border: '1px dashed rgba(255,255,255,0.2)',
                    borderRadius: '8px', color: '#fff', fontSize: '13px',
                    cursor: 'pointer'
                  }}
                />
              </div>
            )}

            {/* Duration / Steps */}
            <div style={S.row}>
              <div style={S.field}>
                <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 6 }}>
                  <label style={{ ...S.label, margin: 0 }}>
                    Duration: {durationAuto ? 'Auto' : `${duration}s`}
                  </label>
                  <Toggle on={durationAuto} onChange={() => { setDurationAuto(p => !p); triggerHaptic('light'); }} />
                </div>
                <input type="range" min={10} max={240} step={5} value={duration}
                  disabled={durationAuto}
                  onChange={e => setDuration(+e.target.value)}
                  style={{ ...S.slider, opacity: durationAuto ? 0.35 : 1 }} />
              </div>
              <div style={S.field}>
                <label style={S.label}>Steps: {steps}</label>
                <input type="range" min={1} max={20} step={1} value={steps}
                  onChange={e => setSteps(+e.target.value)} style={S.slider} />
              </div>
            </div>

            {/* Guidance / Language */}
            <div style={S.row}>
              <div style={S.field}>
                <label style={S.label}>Guidance: {guidance.toFixed(1)}</label>
                <input type="range" min={1} max={15} step={0.5} value={guidance}
                  onChange={e => setGuidance(+e.target.value)} style={S.slider} />
              </div>
              <div style={S.field}>
                <label style={S.label}>Language</label>
                <select style={S.select} value={vocLang} onChange={e => setVocLang(e.target.value)}>
                  {['en','zh','ja','pt','he','de','fr','es','ko','ru','pl'].map(l => (
                    <option key={l} value={l}>{l.toUpperCase()}</option>
                  ))}
                </select>
              </div>
            </div>

            {/* Format / Thinking */}
            <div style={S.row}>
              <div style={S.field}>
                <label style={S.label}>Format</label>
                <select style={S.select} value={format}
                  onChange={e => setFormat(e.target.value as AudioFormat)}>
                  <option value="mp3">MP3</option>
                  <option value="wav">WAV</option>
                  <option value="flac">FLAC</option>
                </select>
              </div>
              <div style={S.field}>
                <label style={S.label}>CoT Thinking</label>
                <div style={{ marginTop: 8 }}>
                  <Toggle on={thinking} onChange={() => { setThinking(p => !p); triggerHaptic('light'); }} />
                </div>
              </div>
            </div>

            {/* Enhance toggles */}
            <div style={S.row}>
              <div style={S.field}>
                <label style={S.label}>Enhance Caption</label>
                <div style={{ marginTop: 8 }}>
                  <Toggle on={enhanceCaption} onChange={() => { setEnhanceCaption(p => !p); triggerHaptic('light'); }} />
                </div>
              </div>
              <div style={S.field}>
                <label style={S.label}>Enhance Lyrics</label>
                <div style={{ marginTop: 8 }}>
                  <Toggle on={enhanceLyrics} onChange={() => { setEnhanceLyrics(p => !p); triggerHaptic('light'); }} />
                </div>
              </div>
            </div>

            {/* Music Metadata - New in 1.5 */}
            {taskType === 'text2music' && (
              <>
                <div style={S.row}>
                  <div style={S.field}>
                    <label style={S.label}>BPM (auto/number)</label>
                    <input style={S.select} value={bpm} onChange={e => setBpm(e.target.value)} placeholder="auto" />
                  </div>
                  <div style={S.field}>
                    <label style={S.label}>Key (auto/C Maj...)</label>
                    <input style={S.select} value={keyScale} onChange={e => setKeyScale(e.target.value)} placeholder="auto" />
                  </div>
                </div>
                <div style={S.row}>
                  <div style={S.field}>
                    <label style={S.label}>Time Sig</label>
                    <select style={S.select} value={timeSignature} onChange={e => setTimeSig(e.target.value)}>
                      {['4/4','3/4','2/4','5/4','6/8','12/8'].map(ts => (
                        <option key={ts} value={ts}>{ts}</option>
                      ))}
                    </select>
                  </div>
                  <div style={S.field}>
                    <label style={S.label}>Track Part</label>
                    <select style={S.select} value={trackName} onChange={e => setTrackName(e.target.value)}>
                      {TRACK_PARTS.map(part => (
                        <option key={part} value={part}>
                          {part.replace(/_/g, ' ').replace(/\b\w/g, c => c.toUpperCase())}
                        </option>
                      ))}
                    </select>
                  </div>
                </div>
              </>
            )}

            {/* Error */}
            {genError && <div style={S.errorBox}>⚠️ {genError}</div>}

            {/* Progress */}
            {generating && (
              <div style={{ marginBottom: 12 }}>
                <div style={{ fontSize: 12, color: 'rgba(255,255,255,0.4)', marginBottom: 4 }}>
                  Generating… this may take a while ☕
                </div>
                <div style={S.progressWrap}>
                  <div style={progressBarStyle(progress)} />
                </div>
              </div>
            )}

            {/* Generate button */}
            <button style={generateBtnStyle(generating || !prompt.trim())}
              onClick={handleGenerate}
              disabled={generating || !prompt.trim()}>
              {generating
                ? <><span style={{ animation: 'spin 1s linear infinite', display: 'inline-block' }}>⏳</span> Generating…</>
                : <>✨ Generate</>}
            </button>
          </div>
        )}

        {/* ════════ LIBRARY ════════ */}
        {tab === 'library' && (
          <div>
            {libraryError && <div style={S.errorBox}>⚠️ {libraryError}</div>}
            {library.length === 0 ? (
              <div style={S.emptyState}>
                <div style={{ fontSize: 40, marginBottom: 12 }}>🎧</div>
                <div>No tracks yet — use Generate to create one.</div>
              </div>
            ) : library.map(track => (
              <div key={track.id} style={S.trackCard}>
                <div style={S.trackMeta}>
                  <div style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
                    <span>{track.taskType === 'text2music' ? '🎵' : '🔊'}</span>
                    <span style={S.badge}>{track.format.toUpperCase()}</span>
                  </div>
                  <span style={S.trackLabel}>{fmtTime(track.createdAt)}</span>
                </div>

                <div style={S.trackPrompt}>{track.prompt}</div>

                <div style={{ display: 'flex', gap: 8 }}>
                  <button onClick={() => handlePlay(track)}
                    style={{
                      flex: 1, padding: '9px', borderRadius: 10, border: 'none',
                      background: playingId === track.id
                        ? 'linear-gradient(135deg,#7c3aed,#2563eb)'
                        : 'rgba(255,255,255,0.08)',
                      color: '#fff', fontSize: 13, fontWeight: 600, cursor: 'pointer',
                      display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 6,
                    }}>
                    {playingId === track.id ? '⏸ Pause' : '▶ Play'}
                  </button>

                  <button type="button"
                    onClick={() => handleDownload(track)}
                    style={{
                      padding: '9px 12px', borderRadius: 10,
                      border: 'none',
                      background: 'rgba(255,255,255,0.06)',
                      color: 'rgba(255,255,255,0.6)',
                      fontSize: 16,
                      display: 'flex', alignItems: 'center',
                      cursor: 'pointer',
                    }}>
                    ⬇
                  </button>

                  <button onClick={() => deleteTrack(track.id)}
                    style={{
                      padding: '9px 12px', borderRadius: 10, border: 'none',
                      background: 'rgba(248,113,113,0.1)', color: '#fca5a5',
                      fontSize: 16, cursor: 'pointer',
                    }}>
                    🗑
                  </button>
                </div>
              </div>
            ))}
          </div>
        )}

        {/* ════════ STATUS ════════ */}
        {tab === 'status' && (
          <div>
            <div style={S.statusCard}>
              <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 12 }}>
                <span style={{ fontSize: 14, fontWeight: 600 }}>AceStep Container</span>
                <div style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
                  <div style={statusDotStyle(online === true)} />
                  <span style={{ fontSize: 12, color: 'rgba(255,255,255,0.5)' }}>
                    {online === null ? 'checking…' : online ? 'Online' : 'Offline'}
                  </span>
                </div>
              </div>

              <div style={{ fontSize: 12, color: 'rgba(255,255,255,0.35)', marginBottom: 8 }}>
                Docker image: ghcr.io/ace-step/ace-step-1.5
              </div>
              <div style={{ fontSize: 12, color: 'rgba(255,255,255,0.35)', marginBottom: 16 }}>
                Proxy: Gradio 6 via gradio_client → FastAPI /v1/acestep/*
              </div>

              <button onClick={() => { checkStatus(); triggerHaptic('light'); }}
                disabled={checkingStatus}
                style={{
                  width: '100%', padding: '10px', borderRadius: 10,
                  border: '1px solid rgba(124,58,237,0.4)',
                  background: 'rgba(124,58,237,0.1)', color: '#a78bfa',
                  fontSize: 13, fontWeight: 600,
                  cursor: checkingStatus ? 'not-allowed' : 'pointer',
                }}>
                {checkingStatus ? 'Checking…' : '↺ Refresh Status'}
              </button>
            </div>

            <div style={S.statusCard}>
              <div style={{ fontSize: 13, fontWeight: 600, marginBottom: 10, color: '#a78bfa' }}>
                ⚙️ Model Info
              </div>
              {([
                ['Model',      'AceStep v1.5 Turbo'],
                ['LM Backend', 'PyTorch (pt)'],
                ['LM Model',   'acestep-5Hz-lm-4B'],
                ['Port',       '7860 (Gradio UI)'],
                ['API Proxy',  '/v1/acestep/*'],
              ] as [string, string][]).map(([k, v]) => (
                <div key={k} style={{ display: 'flex', justifyContent: 'space-between', marginBottom: 6 }}>
                  <span style={{ fontSize: 12, color: 'rgba(255,255,255,0.4)' }}>{k}</span>
                  <span style={{ fontSize: 12, color: '#e8e8f8', fontWeight: 500 }}>{v}</span>
                </div>
              ))}
            </div>

            <div style={S.statusCard}>
              <div style={{ fontSize: 13, fontWeight: 600, marginBottom: 10, color: '#60a5fa' }}>
                🎵 Session Stats
              </div>
              <div style={{ display: 'flex', justifyContent: 'space-between' }}>
                <span style={{ fontSize: 12, color: 'rgba(255,255,255,0.4)' }}>Tracks generated</span>
                <span style={{ fontSize: 12, color: '#e8e8f8', fontWeight: 600 }}>{library.length}</span>
              </div>
            </div>
          </div>
        )}
      </div>

      <style>{`@keyframes spin { to { transform: rotate(360deg); } }`}</style>
    </div>
  );
}
